import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTable, useSortBy, useRowSelect } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faTrash,
  faEnvelope,
  faFilePdf,
  faCheck,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  CubeIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { BASE_URL } from "../../config";
import Swal from "sweetalert2";
import PlanBadge from "../PlanBadge";

const Badge = ({ children, color = "gray" }) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    green: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    yellow: "bg-amber-100 text-amber-700 ring-amber-200",
    blue: "bg-blue-100 text-blue-700 ring-blue-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
        colors[color]
      )}
    >
      {children}
    </span>
  );
};

const ApeScheduleViewer = () => {
  const { id } = useParams(); // setupId from URL
  const navigate = useNavigate();
  const [setupData, setSetupData] = useState(null);
  const [schedulesData, setSchedulesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("start_date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [setupStatus, setSetupStatus] = useState(null); // Draft or Final
  const [viewSchedule, setViewSchedule] = useState(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  // Fetch setup details and schedules
  const fetchSetupAndSchedules = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch setup details
      const setupRes = await axios.get(`${BASE_URL}/ape-scheduler/setups/${id}`, {
        headers: getAuthHeaders(),
      });
      const setup = setupRes.data.setup;
      setSetupData(setup);
      setSetupStatus(setup.status_flag);

      // Fetch schedules for this setup
      const schedulesRes = await axios.get(`${BASE_URL}/ape-scheduler/setups/${id}/schedules`, {
        headers: getAuthHeaders(),
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchQuery,
          sort_by: sortBy,
          sort_direction: sortDirection,
        },
      });
      setSchedulesData(schedulesRes.data.data || []);
      setTotalCount(schedulesRes.data.pagination?.total || 0);
    } catch (error) {
      toast.error("Failed to fetch setup details.", { autoClose: 3000 });
      setTimeout(() => navigate("/ape-scheduler"), 2000);
    } finally {
      setLoading(false);
    }
  }, [id, currentPage, itemsPerPage, searchQuery, sortBy, sortDirection, navigate]);

  useEffect(() => {
    if (id) {
      fetchSetupAndSchedules();
    }
  }, [fetchSetupAndSchedules]);

  useEffect(() => {
    if (!viewSchedule) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setViewSchedule(null);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [viewSchedule]);

  const handleRowSelect = (originalRow) => {
    if (!originalRow || !originalRow.tbl_id || !originalRow.status_flag) return;

    const rowStatus = originalRow.status_flag;

    if (selectedRows.size === 0) {
      setSelectedStatus(rowStatus);
      const newSelected = new Set([originalRow.tbl_id]);
      setSelectedRows(newSelected);
      return;
    }

    if (selectedStatus !== rowStatus) {
      toast.warning(
        `Cannot select ${rowStatus} rows when ${selectedStatus} rows are already selected.`
      );
      return;
    }

    const newSelected = new Set(selectedRows);
    if (newSelected.has(originalRow.tbl_id)) {
      newSelected.delete(originalRow.tbl_id);
      if (newSelected.size === 0) {
        setSelectedStatus(null);
      }
    } else {
      newSelected.add(originalRow.tbl_id);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = () => {
    if (schedulesData.length === 0) return;

    const firstRowStatus = schedulesData[0]?.status_flag;
    if (
      !firstRowStatus ||
      (selectedStatus && selectedStatus !== firstRowStatus)
    ) {
      toast.warning(
        "Cannot select all: mixed statuses or incompatible with current selection."
      );
      return;
    }

    const newSelected = new Set(selectedRows);
    const allSameStatus = schedulesData.every(
      (row) => row.status_flag === firstRowStatus
    );
    if (!allSameStatus) {
      toast.warning("Cannot select all: mixed statuses in current page.");
      return;
    }

    if (selectedStatus === null) {
      setSelectedStatus(firstRowStatus);
    }

    schedulesData.forEach((row) => {
      if (row && row.tbl_id) {
        if (selectedRows.has(row.tbl_id)) {
          newSelected.delete(row.tbl_id);
        } else {
          newSelected.add(row.tbl_id);
        }
      }
    });

    if (newSelected.size === 0) {
      setSelectedStatus(null);
    }

    setSelectedRows(newSelected);
  };

  const handleAction = async (label, setupId) => {
    if (isProcessing) return;

    if (label === "Finalize & Generate LOA") {
      const result = await Swal.fire({
        title: "Finalize schedule and Generate LOA?",
        text: "Would you like finalize the schedule? This action will also generate APE LOA for the selected members.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Finalize & Generate LOA",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#10B981",
      });

      if (result.isConfirmed) {
        setIsProcessing(true);
        try {
          await axios.post(
            `${BASE_URL}/ape-scheduler/finalize-loa`,
            { setup_ids: [setupId] },
            { headers: getAuthHeaders() }
          );
          toast.success(
            "Final Schedule has been Saved! Final Schedule can now be viewed and sent to the corporate."
          );
          fetchSetupAndSchedules();
          setSelectedRows(new Set());
          setSelectedStatus(null);
        } catch (error) {
          toast.error("Failed to finalize and generate LOA.");
        } finally {
          setIsProcessing(false);
        }
      }
      return;
    }

    // Placeholder for other actions
    if (label === "Generate Corporate PDF") {
      toast.info("Generate Corporate PDF - Placeholder");
    } else if (label === "Generate Provider PDF") {
      toast.info("Generate Provider PDF - Placeholder");
    } else if (label === "Send Email To Corporate") {
      await handleSendEmail(setupId, "corporate");
    } else if (label === "Send Email To Providers") {
      await handleSendEmail(setupId, "providers");
    }

    alert(`${label}: Setup ${setupId}`);
  };

  const handleSendEmail = async (setupId, type) => {
    try {
      await axios.post(
        `${BASE_URL}/ape-scheduler/send-email/setup/${setupId}`,
        { type },
        { headers: getAuthHeaders() }
      );
      toast.success(`Email sent to ${type} successfully`, { autoClose: 3000 });
    } catch (error) {
      toast.error(`Failed to send email to ${type}.`, { autoClose: 3000 });
    }
  };

  const handleDelete = async (scheduleId) => {
    const result = await Swal.fire({
      title: "Confirm Delete",
      text: "Are you sure you want to delete this schedule? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        await axios.delete(
          `${BASE_URL}/ape-scheduler/schedules/${scheduleId}`,
          { headers: getAuthHeaders() }
        );
        toast.success("Schedule deleted successfully", { autoClose: 3000 });
        fetchSetupAndSchedules();
      } catch (error) {
        toast.error("Failed to delete schedule.", { autoClose: 3000 });
      } finally {
        setLoading(false);
      }
    }
  };

  const renderActions = (row) => {
    const status = setupStatus; // Use setup status for action visibility
    const scheduleId = row.original.tbl_id;

    if (status === "Draft") {
      return (
        <div className="flex flex-row gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(scheduleId);
            }}
            title="Delete"
            className="rounded-md p-1 text-rose-600 hover:bg-rose-50 hover:text-rose-800"
          >
            <FontAwesomeIcon icon={faTrash} className="h-5 w-5" />
          </button>
        </div>
      );
    } else if (status === "Final") {
      return (
        <div className="flex flex-row gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setViewSchedule(row.original);
            }}
            title="View"
            className="rounded-md p-1 text-blue-600 hover:bg-blue-50 hover:text-blue-800"
          >
            <FontAwesomeIcon icon={faEye} className="h-5 w-5" />
          </button>
        </div>
      );
    }

    return null;
  };

  const columns = useMemo(
    () => [
      {
        id: "selection",
        Header: ({ getToggleAllRowsSelectedProps }) => (
          <input
            type="checkbox"
            {...getToggleAllRowsSelectedProps()}
            onChange={handleSelectAll}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            disabled={isProcessing}
          />
        ),
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedRows.has(row.original?.tbl_id)}
            onChange={() => handleRowSelect(row.original)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            disabled={isProcessing}
          />
        ),
        disableSortBy: true,
      },
      {
        Header: "Action",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => renderActions(row),
      },
      {
        Header: "Corporate",
        accessor: "corp_name",
        Cell: ({ value }) => (
          <span className="font-medium text-gray-900">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Provider",
        accessor: "provider_name",
        Cell: ({ value }) => (
          <span className="text-gray-900">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Coverage",
        accessor: "coverage_code",
        Cell: ({ value }) => <span className="text-gray-700">{value || "N/A"}</span>,
      },
      {
        Header: "Plan",
        accessor: "plan_name",
        Cell: ({ row, value }) => {
          const displayPlan = value || row.original?.plan_code;

          return (
            <span className="text-gray-700">
              {displayPlan === "All"
                ? "All"
                : displayPlan
                ? <PlanBadge value={displayPlan} />
                : "N/A"}
            </span>
          );
        },
      },
      {
        Header: "Member Type",
        accessor: "member_type",
        Cell: ({ value }) => (
          <Badge color={value === "All" ? "green" : "blue"}>
            {value || "N/A"}
          </Badge>
        ),
      },
      {
        Header: "Member ID",
        accessor: "member_id",
        Cell: ({ value }) => <span className="font-mono text-sm">{value || "N/A"}</span>,
      },
      {
        Header: "Member Name",
        accessor: "member_name",
        Cell: ({ value }) => <span className="text-gray-900">{value || "N/A"}</span>,
      },
      {
        Header: "Date From",
        accessor: "start_date",
        Cell: ({ value }) =>
          value ? new Date(value).toLocaleDateString() : "N/A",
      },
      {
        Header: "Date To",
        accessor: "end_date",
        Cell: ({ value }) =>
          value ? new Date(value).toLocaleDateString() : "N/A",
      },
      {
        Header: "Remarks",
        accessor: "remarks",
        Cell: ({ value }) => (
          <span className="text-gray-600 max-w-xs truncate">{value || "N/A"}</span>
        ),
      },
    ],
    [selectedRows, setupStatus, isProcessing]
  );

  const pageCount = useMemo(() => Math.ceil(totalCount / itemsPerPage), [totalCount, itemsPerPage]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: schedulesData,
      manualPagination: true,
      pageCount,
    },
    useSortBy,
    useRowSelect
  );

  // Manual pagination functions
  const previousPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const nextPage = () => setCurrentPage((prev) => Math.min(prev + 1, pageCount));
  const gotoPageInternal = (page) => setCurrentPage(Math.max(1, Math.min(page, pageCount)));

  const canPreviousPage = currentPage > 1;
  const canNextPage = currentPage < pageCount;

  const getPageNumbers = () => {
    const totalPages = pageCount;
    const maxPagesToShow = 5;
    const pages = [];
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  if (!setupData) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <Skeleton height={400} />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <ToastContainer position="top-right" />
      <div className="mb-6">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-xl bg-gray-600 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 mb-4"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
          Back to List
        </motion.button>
        <div className="flex items-center gap-3">
          <CubeIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              APE Schedule Viewer - {setupData.corp_name}
            </h1>
            <p className="text-sm text-gray-500">
              Setup ID: {id} | Status: <Badge color={setupStatus === "Draft" ? "yellow" : "green"}>{setupStatus}</Badge>
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search members, remarks..."
            className="w-full rounded-md border border-gray-200 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            disabled={isProcessing}
          />
        </div>
        <select
          value={itemsPerPage}
          onChange={(e) => {
            setItemsPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
          className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 sm:w-32"
          disabled={isProcessing}
        >
          {[10, 25, 50, 100].map((size) => (
            <option key={size} value={size}>
              {size} per page
            </option>
          ))}
        </select>
      </div>

      {/* Selected Actions - Now shown based on setupStatus only */}
 

      <div className="rounded-xl bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-4">
            <Skeleton count={10} height={40} className="mb-2" />
          </div>
        ) : schedulesData.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            No schedules found for this setup.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table {...getTableProps()} className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          <div className="flex items-center">
                            {column.render("Header")}
                            {column.canSort && (
                              <span className="ml-1">
                                {column.isSorted ? (
                                  column.isSortedDesc ? (
                                    <svg
                                      className="h-5 w-5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                                      />
                                    </svg>
                                  ) : (
                                    <svg
                                      className="h-5 w-5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M5 10l7-7m0 0l7 7m-7-7v18"
                                      />
                                    </svg>
                                  )
                                ) : (
                                  <svg
                                    className="h-5 w-5 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M7 7l5-5m0 0l5 5m-5-5v18"
                                    />
                                  </svg>
                                )}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        className={clsx(
                          "hover:bg-gray-50 transition-colors cursor-pointer",
                          selectedRows.has(row.original?.tbl_id) && "bg-blue-50"
                        )}
                      >
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 p-3 text-sm text-gray-700 sm:flex-row bg-gray-50">
              <div className="text-xs text-gray-600">
                Showing {totalCount ? (currentPage - 1) * itemsPerPage + 1 : 0}{" "}
                to {Math.min(currentPage * itemsPerPage, totalCount)} of{" "}
                {totalCount.toLocaleString()} entries
              </div>
              <div className="flex flex-wrap items-center gap-1">
                <button
                  onClick={() => gotoPageInternal(1)}
                  disabled={!canPreviousPage || isProcessing}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    !canPreviousPage || isProcessing
                      ? "bg-gray-100 text-gray-400"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  <ChevronDoubleLeftIcon className="h-4 w-4" />
                  First
                </button>
                <button
                  onClick={previousPage}
                  disabled={!canPreviousPage || isProcessing}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    !canPreviousPage || isProcessing
                      ? "bg-gray-100 text-gray-400"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  Previous
                </button>
                {getPageNumbers().map((page) => (
                  <button
                    key={page}
                    onClick={() => gotoPageInternal(page)}
                    disabled={isProcessing}
                    className={clsx(
                      "rounded-md px-2 py-1 text-xs",
                      currentPage === page
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200",
                      isProcessing && "cursor-not-allowed opacity-50"
                    )}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={nextPage}
                  disabled={!canNextPage || isProcessing}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    !canNextPage || isProcessing
                      ? "bg-gray-100 text-gray-400"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  Next
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => gotoPageInternal(pageCount)}
                  disabled={!canNextPage || isProcessing}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    !canNextPage || isProcessing
                      ? "bg-gray-100 text-gray-400"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  Last
                  <ChevronDoubleRightIcon className="h-4 w-4" />
                </button>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  disabled={isProcessing}
                  className="ml-1 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
                >
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {viewSchedule && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setViewSchedule(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="schedule-details-title"
          >
            <motion.div
              className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
            >
              <div className="flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white">
                <div>
                  <h2 id="schedule-details-title" className="text-lg font-semibold">
                    APE Schedule Details
                  </h2>
                  <p className="mt-1 text-xs text-blue-100">
                    Current data for schedule #{viewSchedule.tbl_id || "N/A"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setViewSchedule(null)}
                  aria-label="Close schedule details"
                  className="rounded-md px-2 py-1 text-2xl leading-none text-white hover:bg-white/15"
                >
                  &times;
                </button>
              </div>

              <div className="overflow-y-auto p-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {[
                    ["Schedule ID", viewSchedule.tbl_id],
                    ["Status", viewSchedule.status_flag],
                    ["Corporate", viewSchedule.corp_name],
                    ["Provider", viewSchedule.provider_name],
                    ["Coverage", viewSchedule.coverage_code],
                    ["Plan", viewSchedule.plan_name || viewSchedule.plan_code],
                    ["Member Type", viewSchedule.member_type],
                    ["Member ID", viewSchedule.member_id],
                    ["Member Name", viewSchedule.member_name],
                    [
                      "Date From",
                      viewSchedule.start_date
                        ? new Date(viewSchedule.start_date).toLocaleDateString()
                        : null,
                    ],
                    [
                      "Date To",
                      viewSchedule.end_date
                        ? new Date(viewSchedule.end_date).toLocaleDateString()
                        : null,
                    ],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {label}
                      </div>
                      <div className="mt-1 break-words text-sm font-medium text-gray-900">
                        {value || "N/A"}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Remarks
                  </div>
                  <div className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-900">
                    {viewSchedule.remarks || "N/A"}
                  </div>
                </div>
              </div>

              <div className="flex justify-end border-t border-gray-200 bg-gray-50 px-6 py-4">
                <button
                  type="button"
                  onClick={() => setViewSchedule(null)}
                  className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ApeScheduleViewer;
